// Push Notification Service for PWA
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

class PushNotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.isSupported = false;
    this.isSubscribed = false;
  }

  // Check if push notifications are supported
  checkSupport() {
    this.isSupported = 
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window;
    return this.isSupported;
  }

  // Convert VAPID key for subscription
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Request notification permission
  async requestPermission() {
    if (!this.checkSupport()) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      console.log('Notification permission:', permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }

  // Initialize push notifications
  async initialize() {
    if (!this.checkSupport()) {
      console.log('Push notifications not supported');
      return false;
    }

    try {
      // Wait for service worker to be ready
      this.registration = await navigator.serviceWorker.ready;
      console.log('Service Worker ready');

      // Check if already subscribed
      this.subscription = await this.registration.pushManager.getSubscription();
      
      if (this.subscription) {
        console.log('Already subscribed to push notifications');
        this.isSubscribed = true;
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return false;
    }
  }

  // Subscribe to push notifications
  async subscribe() {
    if (!this.checkSupport()) {
      console.log('Push notifications not supported');
      return null;
    }

    try {
      // Request permission first
      const hasPermission = await this.requestPermission();
      if (!hasPermission) {
        console.log('Notification permission denied');
        return null;
      }

      // Get VAPID public key from backend
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/notifications/vapid-key`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const vapidPublicKey = response.data.publicKey;

      // Subscribe to push notifications
      this.subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('Push subscription created:', this.subscription);

      // Send subscription to backend
      await this.sendSubscriptionToBackend(this.subscription);

      this.isSubscribed = true;
      return this.subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      return null;
    }
  }

  // Send subscription to backend
  async sendSubscriptionToBackend(subscription) {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/notifications/subscribe`,
        { subscription },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      console.log('Subscription sent to backend');
    } catch (error) {
      console.error('Error sending subscription to backend:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    if (!this.subscription) {
      console.log('No active subscription to unsubscribe from');
      return true;
    }

    try {
      // Unsubscribe from push manager
      await this.subscription.unsubscribe();
      
      // Remove subscription from backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/notifications/unsubscribe`,
        { endpoint: this.subscription.endpoint },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      this.subscription = null;
      this.isSubscribed = false;
      console.log('Unsubscribed from push notifications');
      return true;
    } catch (error) {
      console.error('Error unsubscribing from push notifications:', error);
      return false;
    }
  }

  // Check subscription status
  async checkSubscription() {
    if (!this.checkSupport()) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      this.subscription = await registration.pushManager.getSubscription();
      this.isSubscribed = !!this.subscription;
      return this.isSubscribed;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return false;
    }
  }

  // Show local notification (fallback for non-push scenarios)
  showNotification(title, options = {}) {
    if (!this.checkSupport()) {
      console.log('Notifications not supported');
      return;
    }

    if (Notification.permission === 'granted' && this.registration) {
      this.registration.showNotification(title, {
        badge: '/logo192.png',
        icon: '/logo192.png',
        ...options
      });
    }
  }
}

// Export singleton instance
const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
